/**
 * Normalizador central de lectura de reportes.
 *
 * Los reportes digitales guardan sus metadatos dentro de `respuesta`, mientras
 * que los escaneados los guardan en el nivel superior. Estas funciones resuelven
 * cada campo probando ambas estructuras para que el resto de la UI consuma un
 * único contrato, sin migrar los reportes ya creados.
 */

function firstDefined(candidates) {
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && candidate !== "") {
      return candidate;
    }
  }
  return null;
}

/** ID de planta, probando estructura plana, `respuesta` y objetos anidados. */
export function getReportPlantId(report) {
  if (!report) return null;

  const candidates = [
    report.idplanta,
    report?.respuesta?.idplanta,
    report?.respuesta?.planta?.idplanta,
    report?.respuesta?.planta,
    report?.planta?.idplanta,
    report?.planta,
    report?.datosPlanta?.idplanta,
    report?.datosPlanta,
    report?.idplantaempresa,
    report?.plantaId,
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === "") {
      continue;
    }

    if (typeof candidate === "object") {
      const nested = firstDefined([candidate.idplanta, candidate.id]);
      if (nested !== null) return nested;
      continue;
    }

    return candidate;
  }

  return null;
}

/** Nombre de planta; usa el catálogo cuando solo hay ID (reportes escaneados). */
export function getReportPlantName(report, plantas = []) {
  if (!report) return "N/A";

  const nestedName = firstDefined([
    report?.respuesta?.planta?.nombreplanta,
    typeof report?.respuesta?.planta === "string" ? report.respuesta.planta : null,
    report?.planta?.nombreplanta,
    report?.datosPlanta?.nombreplanta,
    report?.planta?.nombre,
  ]);
  if (nestedName) return nestedName;

  const plantId = getReportPlantId(report);
  if (plantId !== null) {
    const planta = plantas.find(
      (p) => String(p.idplanta) === String(plantId),
    );
    if (planta?.nombreplanta) return planta.nombreplanta;
  }

  return "N/A";
}

/** Nombre del inspector; usa el catálogo de usuarios cuando solo hay ID. */
export function getReportInspectorName(report, usuarios = []) {
  if (!report) return "N/A";

  const nombreDirecto = firstDefined([
    report?.respuesta?.inspector,
    typeof report?.inspector === "string" ? report.inspector : null,
  ]);
  if (nombreDirecto) return nombreDirecto;

  const inspectorId = firstDefined([
    report?.idinspector,
    report?.respuesta?.idinspector,
  ]);
  if (inspectorId !== null) {
    const user = usuarios.find(
      (u) => String(u.idusuario) === String(inspectorId),
    );
    if (user?.nombreusuario) return user.nombreusuario;
  }

  return "N/A";
}

/** ID de tipo de mantenimiento, probando nivel superior y `respuesta`. */
export function getReportTipoId(report) {
  if (!report) return null;
  return firstDefined([
    report?.idtipomantenimiento,
    report?.respuesta?.idtipomantenimiento,
  ]);
}

/** Nombre del tipo de mantenimiento; resuelve texto, objeto anidado o catálogo. */
export function getReportTipoName(report, tiposMantenimiento = []) {
  if (!report) return "Rutinario";

  const respuestaTipo = report?.respuesta?.tipoMantenimiento;
  if (respuestaTipo && typeof respuestaTipo === "object") {
    return respuestaTipo.tipomantenimiento || "Rutinario";
  }
  if (typeof respuestaTipo === "string" && respuestaTipo.trim()) {
    return respuestaTipo;
  }

  const tipoId = getReportTipoId(report);
  if (tipoId !== null) {
    const tipo = tiposMantenimiento.find(
      (t) => String(t.idtipomantenimiento) === String(tipoId),
    );
    if (tipo?.tipomantenimiento) return tipo.tipomantenimiento;
  }

  return "Rutinario";
}

/** ID de frecuencia; usa el declarado o lo deriva desde el tipo de mantenimiento. */
export function getReportFrequencyId(report, tiposMantenimiento = []) {
  if (!report) return null;

  const directFrequencyId = firstDefined([
    report?.idfrecuenciamantenimiento,
    report?.respuesta?.idfrecuenciamantenimiento,
  ]);
  if (directFrequencyId !== null) return directFrequencyId;

  const tipoId = getReportTipoId(report);
  if (tipoId === null) return null;

  const tipo = tiposMantenimiento.find(
    (t) => String(t.idtipomantenimiento) === String(tipoId),
  );
  return tipo?.idfrecuenciamantenimiento ?? null;
}

/** Fecha del reporte como cadena "YYYY-MM-DD". */
export function getReportDate(report) {
  if (!report) return null;

  const scannDate = report?.fechacreacion
    ? String(report.fechacreacion).split("T")[0]
    : null;
  const digitalDate = report?.respuesta?.fecha || null;

  return report?.isScann
    ? scannDate || digitalDate
    : digitalDate || scannDate;
}

/** Fecha del reporte como objeto Date, para ordenamientos. */
export function getReportDateObject(report) {
  if (report?.isScann && report?.fechacreacion) {
    return new Date(report.fechacreacion);
  }
  const digitalDate = report?.respuesta?.fecha;
  if (digitalDate) return new Date(`${digitalDate}T00:00:00`);
  if (report?.fechacreacion) return new Date(report.fechacreacion);
  return new Date(0);
}
